<template>
    <div class="py-12">
        <div class="flex flex-wrap justify-between">
            <h1
                class="text-2xl font-medium leading-normal tracking-small text-gray-800"
            >
                {{ $t("vue.blacklists") }}
            </h1>
        </div>

        <template v-if="ipsBlacklistedCount > 0">
            <div class="mt-5 rounded-md border border-red-500 bg-red-50 p-4">
                <div class="flex">
                    <div class="shrink-0">
                        <XCircleIcon
                            class="h-5 w-5 text-red-400"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm text-red-800">
                            {{ $t("vue.we_found_following") }}
                            <strong
                                >{{ ipsBlacklistedCount }}
                                {{ $t("vue.ips_blacklisted_count") }}</strong
                            >
                            {{ $t("vue.on_blacklists") }}
                        </h3>
                    </div>
                </div>
            </div>

            <div class="mt-10 rounded-lg bg-white py-10 shadow">
                <div class="px-4 sm:px-6 lg:px-8">
                    <div class="flow-root">
                        <div
                            class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8"
                        >
                            <div
                                class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
                            >
                                <table
                                    class="min-w-full divide-y divide-gray-300"
                                >
                                    <thead>
                                        <tr>
                                            <th
                                                scope="col"
                                                class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ $t("vue.sender") }}
                                            </th>
                                            <th
                                                scope="col"
                                                class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ $t("vue.ip") }}
                                            </th>
                                            <th
                                                scope="col"
                                                class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ $t("vue.emails_seen") }}
                                            </th>
                                            <th
                                                scope="col"
                                                class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ $t("vue.last_seen") }}
                                            </th>
                                            <th
                                                scope="col"
                                                class="px-3 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                                            >
                                                {{ $t("vue.blacklisted_at") }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="divide-y divide-gray-200 bg-white"
                                    >
                                        <tr
                                            v-for="ip in ipsBlacklisted"
                                            :key="ip.ip"
                                        >
                                            <td
                                                class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                                            >
                                                <div class="flex items-center">
                                                    <div
                                                        class="mr-3 flex"
                                                        style="
                                                            width: 20px;
                                                            height: 20px;
                                                        "
                                                    >
                                                        <img
                                                            :src="
                                                                ip.sender ==
                                                                'Ecomail'
                                                                    ? 'https://ecomail.cz/images/favicons/favicon-32x32.png'
                                                                    : 'https://www.google.com/s2/favicons?domain=' +
                                                                      ip.sender +
                                                                      '&sz=40'
                                                            "
                                                            :alt="ip.sender"
                                                        />
                                                    </div>
                                                    {{ ip.sender }}
                                                </div>
                                            </td>
                                            <td
                                                class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                                            >
                                                {{ ip.ip }}
                                            </td>
                                            <td
                                                class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                                            >
                                                {{ ip.count }}
                                            </td>
                                            <td
                                                class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                                            >
                                                {{
                                                    formatTimestamp(
                                                        ip.last_seen,
                                                    )
                                                }}
                                            </td>
                                            <td
                                                class="text-md whitespace-nowrap px-3 py-4 text-gray-800"
                                            >
                                                {{ ip.result }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div
                class="mt-5 rounded-md border border-green-500 bg-green-50 p-4"
            >
                <div class="flex">
                    <div class="shrink-0">
                        <CheckCircleIcon
                            class="h-5 w-5 text-green-400"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-green-800">
                            {{ $t("vue.all_ips_clean") }}
                        </p>
                    </div>
                </div>
            </div>
        </template>
    </div>
    <div class="rounded-md border border-blue-500 bg-blue-50 p-4">
        <div class="flex">
            <div class="shrink-0">
                <InformationCircleIcon
                    class="h-5 w-5 text-blue-400"
                    aria-hidden="true"
                />
            </div>
            <div class="ml-3">
                <p class="text-sm text-green-800">
                    {{ $t("vue.we_checked") }}
                    <strong
                        >{{ ipsUsedCount }}
                        {{ $t("vue.ips_used_count") }}</strong
                    >
                    {{ $t("vue.addresses_against_blacklists") }}
                </p>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    CheckCircleIcon,
    XCircleIcon,
    InformationCircleIcon,
} from "@heroicons/vue/20/solid";
import type ISelectable from "@/types/Atomic/ISelectable";
import type { PropType } from "vue";

defineProps({
    ipsUsed: {
        type: Array as PropType<ISelectable[]>,
        default: () => [],
    },
    ipsBlacklisted: {
        type: Array as PropType<ISelectable[]>,
        default: () => [],
    },
    ipsUsedCount: {
        type: Number,
        default: () => 0,
    },
    ipsBlacklistedCount: {
        type: Number,
        default: () => 0,
    },
});

function formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
}
</script>
